import { useMemo, useState } from 'react';
import { criarContaCliente } from '../api';
import { Alert } from './common';

/**
 * Próximo código livre debaixo da conta mãe, no mesmo padrão das irmãs
 * (1.1.01.03.0006 -> 1.1.01.03.0007). Sem irmãs, usa 4 dígitos para analítica
 * e 2 para sintética, que é o padrão do plano da casa (N.N.NN.NN.NNNN).
 */
export function proximoCodigo(mae, contas, tipo) {
  if (!mae) return '';
  const prefixo = mae.codigo + '.';
  const filhas = contas
    .map(c => c.codigo)
    .filter(c => c.startsWith(prefixo) && !c.slice(prefixo.length).includes('.'));
  if (!filhas.length) return prefixo + (tipo === 'A' ? '0001' : '01');
  const ultimos = filhas.map(c => c.slice(prefixo.length));
  const largura = Math.max(...ultimos.map(s => s.length));
  const maior = Math.max(...ultimos.map(s => parseInt(s, 10) || 0));
  return prefixo + String(maior + 1).padStart(largura, '0');
}

/** Formulário de conta nova: escolhe a conta mãe (sintética) e sugere o código. */
export default function NovaConta({ clienteId, contas, onCriada, onFechar }) {
  const sinteticas = useMemo(() => contas.filter(c => c.tipo === 'S'), [contas]);
  const [maeId, setMaeId] = useState('');
  const [tipo, setTipo] = useState('A');
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [natureza, setNatureza] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  const mae = sinteticas.find(c => String(c.id) === String(maeId)) || null;

  const escolherMae = (id) => {
    setMaeId(id);
    const m = sinteticas.find(c => String(c.id) === String(id));
    setCodigo(proximoCodigo(m, contas, tipo));
    setNatureza(m?.natureza || '');
  };
  const escolherTipo = (t) => {
    setTipo(t);
    if (mae) setCodigo(proximoCodigo(mae, contas, t));
  };

  const problema = !mae ? 'Escolha a conta mãe.'
    : !codigo.startsWith(mae.codigo + '.') ? `O código precisa começar com ${mae.codigo}.`
    : !/^\d+(\.\d+)*$/.test(codigo) ? 'O código só aceita números separados por ponto.'
    : contas.some(c => c.codigo === codigo) ? `Já existe a conta ${codigo}.`
    : nome.trim().length < 2 ? 'Informe o nome da conta.'
    : !natureza ? 'Escolha a natureza.'
    : null;

  const salvar = async () => {
    if (problema) return;
    setSalvando(true); setErro(null);
    try {
      const grau = (mae.grau || mae.nivel || codigo.split('.').length - 1) + 1;
      const conta = await criarContaCliente(clienteId, { codigo, nome: nome.trim(), tipo, natureza, grau });
      onCriada?.(conta);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="toolbar" style={{ marginBottom: 8 }}>
        <strong className="card__title">Nova conta</strong>
        <span className="toolbar__spacer" />
        {onFechar && <a href="#" className="small" onClick={e => { e.preventDefault(); onFechar(); }}>fechar</a>}
      </div>
      <div className="toolbar">
        <select value={maeId} onChange={e => escolherMae(e.target.value)} aria-label="Conta mãe">
          <option value="">Conta mãe (sintética)…</option>
          {sinteticas.map(c => <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>)}
        </select>
        <select value={tipo} onChange={e => escolherTipo(e.target.value)} aria-label="Tipo">
          <option value="A">Analítica</option>
          <option value="S">Sintética</option>
        </select>
        <input value={codigo} onChange={e => setCodigo(e.target.value.trim())} placeholder="Código"
          aria-label="Código" style={{ width: 160, fontFamily: 'monospace' }} />
        <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Nome da conta"
          aria-label="Nome" style={{ flex: 1, minWidth: 240 }} />
        <select value={natureza} onChange={e => setNatureza(e.target.value)} aria-label="Natureza">
          <option value="">Natureza…</option>
          <option value="D">Devedora</option>
          <option value="C">Credora</option>
        </select>
        <button className="primary" onClick={salvar} disabled={!!problema || salvando}>
          {salvando ? 'Criando…' : 'Criar conta'}
        </button>
      </div>
      {problema && maeId && <div className="small muted" style={{ marginTop: 6 }}>{problema}</div>}
      {erro && <Alert tipo="danger">{erro}</Alert>}
    </div>
  );
}
